/**
 * Favorite-games query hook (lobby row).
 *
 * Returns full game objects (not just codes) for the Favorites row, keyed by
 * userId. Distinct from the shared `useFavorites` hook, which tracks the set of
 * favorited game codes for the heart toggle across the whole lobby. This hook
 * powers the dedicated row; toggling a favorite invalidates this key so the row
 * refreshes (see useGameLobby).
 */

import { useQuery } from "@tanstack/react-query";
import favoritesService from "../../../shared/services/favoritesService";
import { queryKeys } from "../../../shared/data/queryKeys";
import { normalizeQueryError } from "../../../shared/data/errors";

/**
 * @param {string|undefined} userId - logged-in player id (gates the query)
 * @param {number} [limit]
 */
export function useFavoriteGames(userId, limit = 5) {
  return useQuery({
    queryKey: queryKeys.games.favorites(userId),
    enabled: Boolean(userId),
    queryFn: async () => {
      try {
        const result = await favoritesService.getFavoriteGames({ limit });
        return result.data?.games || [];
      } catch (err) {
        throw normalizeQueryError(err, "Failed to load favorite games");
      }
    },
  });
}

export default useFavoriteGames;
